import ButtonBase from 'components/partials/button'
import InputBase from 'components/partials/inputBase'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import axiosClient from 'network/axiosClient'
import React, { useEffect, useState } from 'react'
import PreviewDialog from './previewDialog'

interface Props {
  sendingEmailSubject?: string | null
  handleStoreHtmlTemplateString: (htmlString: string) => void
  htmlString: string
  index?: number
}

const allowedTypes = ['text/html', 'text/css']
const maxSizeInMB = 5

const UploadHTMLAndCSSFile: React.FC<Props> = ({
  handleStoreHtmlTemplateString,
  sendingEmailSubject,
  index,
  htmlString,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [currentHtmlString, setCurrentHtmlString] = useState<string>(htmlString)
  const [isOpenPreview, setIsOpenPreview] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [isChangeFile, setIsChangeFile] = useState(true)

  const onChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files

    if (!files) return

    const fileArr = Array.from(files)
    const errors: string[] = []
    let hasHtmlFile = false
    fileArr.forEach((file) => {
      const fileSizeInMB = file.size / (1024 * 1024)

      if (!allowedTypes.includes(file.type)) {
        errors.push(`❌ ${file.name}: Invalid file type.`)
      }

      if (fileSizeInMB > maxSizeInMB) {
        errors.push(`❌ ${file.name}: File size exceeds ${maxSizeInMB}MB.`)
      }

      if (file.type === 'text/html') {
        hasHtmlFile = true
      }
    })

    if (!hasHtmlFile) {
      errors.push(`Missing HTML File`)
    }

    if (errors.length > 0) {
      errors.forEach((error) => {
        notify(error, STATUS.ERROR)
      })
    } else {
      setSelectedFiles(fileArr)
      setIsChangeFile(true)
    }
  }

  const onClickPreviewBtn = async () => {
    setIsOpenPreview(true)
    setIsLoadingData(true)

    if (!isChangeFile) {
      setIsLoadingData(false)
      return
    }

    if (selectedFiles.length === 0 && htmlString) {
      setIsLoadingData(false)
      return
    }

    const formData = new FormData()

    Array.from(selectedFiles).forEach((file) => {
      if (file.name.endsWith('.html')) {
        formData.append('html', file)
      } else if (file.name.endsWith('.css')) {
        formData.append('css', file)
      }
    })

    axiosClient
      .post('/agencyGroup/inline-css', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        setCurrentHtmlString(response.data.inlinedHtml)
        setIsLoadingData(false)
        setIsChangeFile(false)
      })
      .catch((error) => {
        if (error?.response?.data?.message) {
          notify(error.response.data.message, STATUS.ERROR)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      })
  }

  const handleChangeHtmlString = (value: string) => {
    setCurrentHtmlString(value)
  }

  useEffect(() => {}, [selectedFiles])

  useEffect(() => {
    handleStoreHtmlTemplateString(currentHtmlString)
  }, [currentHtmlString])

  return (
    <div className="w-full flex justify-between space-x-2">
      <div className="flex w-full space-x-2">
        <div className="flex-col max-w-[120px]">
          {/**Upload file button*/}
          <div>
            <label
              htmlFor={`multiple_files-${index}`}
              className="w-[120px] inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-1 py-1 text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 cursor-pointer"
            >
              <span>Choose files</span>
            </label>

            <input
              // className="text-sm cursor-pointer focus:outline-none content-center"
              className="hidden"
              id={`multiple_files-${index}`}
              accept=".html,.css"
              type="file"
              multiple={true}
              onChange={onChangeFile}
            />
          </div>

          {/* File name display */}
          <div className="truncate">
            <span aria-live="polite" className="text-sm text-slate-600">
              {selectedFiles.length > 0
                ? selectedFiles
                    .map((item) => item.name)
                    .join(', ')
                    .trim()
                : 'No file selected'}
            </span>
          </div>
        </div>

        {/** HTML inline String */}
        <InputBase
          className="w-full"
          name={`html-string-${index}`}
          value={currentHtmlString}
          handleChange={handleChangeHtmlString}
        />
      </div>

      <ButtonBase
        onClick={onClickPreviewBtn}
        disabled={selectedFiles.length === 0 && !currentHtmlString}
        bgDisabled="bg-violet-600"
      >
        Preview
      </ButtonBase>

      {/**Preview Dialog */}
      {sendingEmailSubject && (
        <PreviewDialog
          sendingEmailSubject={sendingEmailSubject}
          index={index}
          handleClosePreviewDialog={() => {
            setIsOpenPreview(false)
          }}
          htmlString={currentHtmlString}
          isLoadingData={isLoadingData}
          isOpenPreview={isOpenPreview}
        />
      )}
    </div>
  )
}

export default UploadHTMLAndCSSFile
